import { ArrowRightIcon, CopyIcon, MapPinIcon, NavigationIcon } from 'lucide-react';
import { toast } from 'sonner';
import { HeadingText } from '../shared/HeadingText';
import { SalonMap } from './SalonMap';
import { hoursLabel, salon } from '../../data/content';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { pageActions } from '../../data/pageActions';
import { isOpen } from '../../core/rules/schedule';
import { salonWeekday, today as todayIso } from '../../core/format/dates';
import { salonDayOn } from '../../core/rules/salonDays';
import { useOverlayActions } from '../../contexts/StoreContext';
import { fill } from '../../utils/copy';
import { btnPrimary, btnSecondary } from '../../ui/styles';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';

/** Id van dit blok: de hero linkt hierheen ("Route en kaart"). */
export const VISIT_ID = 'langskomen';

/**
 * Langskomen: alles wat een nieuwe cliënt vóór haar eerste afspraak wil weten.
 * Volgorde = de vragen die ze in die volgorde stelt: waar is het, hoe kom ik er,
 * wanneer kan ik komen, en dan pas: plan een afspraak.
 */
export function ContactVisit() {
  const pages = usePages();
  const {
    openingHours,
    salonExceptions
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const copy = pages.contact.visit;
  const today = salonWeekday();
  const todayHours = openingHours.find(h => h.weekday === today);
  // Een vakantie- of uitzonderingsdag uit Instellingen gaat voor de vaste week.
  const todaySalon = salonDayOn(todayIso(), openingHours, salonExceptions);
  const address = salon.address || `${salon.street}, ${salon.postalCode} ${salon.locality}`;
  const arrival = [{
    label: 'Openbaar vervoer',
    text: salon.transit
  }, {
    label: 'Parkeren',
    text: salon.parking
  }, {
    label: 'Aanbellen',
    text: salon.entrance
  }, {
    label: 'Toegankelijkheid',
    text: salon.accessibility
  }].filter(a => a.text?.trim());
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Adres gekopieerd', {
        description: address
      });
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: address
      });
    }
  };
  return <Section id={VISIT_ID} tone="blush" sectionClassName="scroll-mt-28" labelledBy="contact-visit" contained={false} data-id="element-7445" data-mp="1hqqhohkygxq">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7446" data-mp="bdtr2pwsa7ik">
        {/* Tekst eerst in de bron: op mobiel lees je eerst waar het is, dan zie je de kaart. */}
        <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1" data-id="element-7447" data-mp="6h3faes0hy2y">
          {/* Op blush haalt kleine clay-tekst geen AA (4,3:1): het regeltje staat in ink. */}
          {copy.eyebrow && <p className="text-[14px] text-ink" data-id="element-7448" data-mp="fdcblkkp0gkv">{fill(copy.eyebrow)}</p>}
          <h2 id="contact-visit" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-7449" data-mp="2m6o9qqoos505">
            <HeadingText heading={copy.heading} data-id="element-7450" data-mp="2h7lfm1a8vj6y" />
          </h2>
          {/* "Alleen op afspraak" is één vaste formulering (ook bij kom langs en op Salon); de zin erna is van Sarah. */}
          <p className="mt-6 max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7451" data-mp="11odngp7w9yu8">
            <span className="text-ink" data-id="element-7452" data-mp="nwfm50kfd78m">{pageActions.byAppointment}.</span>
            {copy.body && <> {fill(copy.body)}</>}
          </p>

          <address className="mt-8 flex gap-3 not-italic" data-id="element-7453" data-mp="rwzlmash0mxx">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7454" data-mp="dy9ncrtx0h7z" />
            <div data-id="element-7455" data-mp="ix336qwockbb">
              <p className="text-[17px] leading-snug text-ink" data-id="element-7456" data-mp="ixmj622rkymp">
                {salon.street}
                <br data-id="element-7457" data-mp="4yd4x7y4celd" />
                {salon.postalCode} {salon.locality}
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-[14px]" data-id="element-7458" data-mp="8ynaiji012zp">
                <a href={salon.routeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 py-1 text-ink underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay hover:underline" data-id="element-7459" data-mp="1zbmf71yho44o">
                  <NavigationIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7460" data-mp="19n79ogvb2gl7" />
                  Plan je route<span className="sr-only" data-id="element-7461" data-mp="14ncjygn9nx1q"> {shopLabels.newTab}</span>
                </a>
                <button type="button" onClick={() => void copyAddress()} className="inline-flex items-center gap-1.5 py-1 text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-7462" data-mp="zq2k072942zn">
                  <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7463" data-mp="lx38eg5lasxd" />
                  Kopieer adres
                </button>
              </div>
            </div>
          </address>

          {arrival.length > 0 && <div className="mt-10" data-id="element-7464" data-mp="byx4xu44xeqe">
              {copy.arriveTitle && <h3 className="type-name text-ink" data-id="element-7465" data-mp="7xv3r7qnqsls">{fill(copy.arriveTitle)}</h3>}
              <dl className="mt-4 divide-y divide-ink/15 border-y border-ink/15" data-id="element-7466" data-mp="2jlqj5xogdr5g">
                {arrival.map(a => <div key={a.label} className="grid gap-1 py-4 sm:grid-cols-[9.5rem_1fr] sm:gap-4" data-id="element-7467" data-mp="2nmagb1910url">
                    <dt className="text-[14px] text-muted" data-id="element-7468" data-mp="2sn6jfclk0cvy">{a.label}</dt>
                    <dd className="text-[15px] leading-relaxed text-ink" data-id="element-7469" data-mp="2xkzmhwrk8qs0">{a.text}</dd>
                  </div>)}
              </dl>
            </div>}

          {copy.cta && <div className="mt-8 flex flex-wrap gap-3" data-id="element-7470" data-mp="24watkl0nrrrc">
              <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimary} data-id="element-7471" data-mp="24wu0z7somxr4">
                {fill(copy.cta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7472" data-mp="2emvs68gc0wa9" />
              </button>
              {copy.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={btnSecondary} data-id="element-7473" data-mp="1h6q1ncmj1k5x">
                  {fill(copy.secondaryCta)}
                  <span className="sr-only" data-id="element-7474" data-mp="1e4lih4pabfzj">{shopLabels.viaWhatsApp}</span>
                </a>}
            </div>}
        </div>

        <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1" data-id="element-7475" data-mp="1a1p7o05zko65">
          <SalonMap loadLabel={fill(copy.mapCta || 'Toon de kaart')} note={fill(copy.mapNote || '')} data-id="element-7476" data-mp="1ntvc4ozs16pr" />

          <div className="mt-8" data-id="element-7477" data-mp="1iwn2jcsp2b04">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-7478" data-mp="1iyfzu1hf3ivg">
              <h3 className="type-name text-ink" data-id="element-7479" data-mp="v8lpprohg8ew">Openingstijden · op afspraak</h3>
              {todayHours && <p className="text-[14px] text-muted" data-id="element-7480" data-mp="154p17o585jei">
                  Vandaag: <span className="text-ink" data-id="element-7481" data-mp="15603tkmxol41">{todaySalon ? `${todaySalon.open} – ${todaySalon.close}` : 'gesloten'}</span>
                </p>}
            </div>
            {/* Twee kolommen op desktop: zeven regels passen naast elkaar en de kaart blijft het grootst. */}
            <dl className="mt-4 grid border-t border-ink/15 text-[15px] sm:grid-cols-2 sm:gap-x-10" data-id="element-7482" data-mp="2bjze6rxbgyen">
              {openingHours.map(h => {
              const isToday = h.weekday === today;
              const open = isOpen(h);
              return <div key={h.day} className={`flex justify-between gap-4 border-b border-ink/15 py-3 ${isToday ? 'font-medium' : ''}`} aria-current={isToday ? 'date' : undefined} data-id="element-7483" data-mp="3ki8yk67jj1tu">
                    <dt className={open || isToday ? 'text-ink' : 'text-muted'} data-id="element-7484" data-mp="h33if3nbvjjo">
                      {h.day}
                      {isToday && <span className="ml-2 text-[13px] font-normal text-ink underline underline-offset-4" data-id="element-7485" data-mp="7b77gs057yy1">vandaag</span>}
                    </dt>
                    <dd className={`tabular-nums ${open ? 'text-ink' : 'text-muted'}`} data-id="element-7486" data-mp="c8z9ieui51fy">{hoursLabel(h)}</dd>
                  </div>;
            })}
            </dl>
            <p className="mt-4 text-[13px] leading-relaxed text-muted" data-id="element-7487" data-mp="14qtgg887moov">
              {salon.name} · KvK {salon.kvk}
              {salon.vatId && <> · Btw-id {salon.vatId}</>}
            </p>
          </div>
        </div>
      </div>
    </Section>;
}